import type { Locale } from '@mfw/translations';

import type {
  ApiMarketplace,
  UiMarketplace,
} from '../../schemas/marketplace.schema';
import type { ApiTranslation } from '../../schemas/translations.schema';

import { mapCategoryWithTagsFromApi } from './categories.mappers';
import { mapTranslationsFromApi } from './translations.mappers';

export function mapMarketplaceFromApi(
  locale: Locale,
  apiMarketplace: ApiMarketplace,
): UiMarketplace | null {
  const translations =
    apiMarketplace?.exercise?.translations ||
    apiMarketplace?.workout?.translations ||
    apiMarketplace?.program?.translations ||
    apiMarketplace?.meal?.translations ||
    apiMarketplace?.nutritionPlan?.translations ||
    apiMarketplace?.episode?.translations ||
    apiMarketplace?.module?.translations ||
    apiMarketplace?.course?.translations;

  const organizationId =
    apiMarketplace?.exercise?.organizationId ||
    apiMarketplace?.workout?.organizationId ||
    apiMarketplace?.program?.organizationId ||
    apiMarketplace?.meal?.organizationId ||
    apiMarketplace?.nutritionPlan?.organizationId ||
    apiMarketplace?.episode?.organizationId ||
    apiMarketplace?.module?.organizationId ||
    apiMarketplace?.course?.organizationId;

  const categories =
    apiMarketplace?.exercise?.categories ||
    apiMarketplace?.workout?.categories ||
    apiMarketplace?.program?.categories ||
    apiMarketplace?.meal?.categories ||
    apiMarketplace?.nutritionPlan?.categories;

  let type: UiMarketplace['type'] = 'exercise';

  if (apiMarketplace?.workout) {
    type = 'workout';
  } else if (apiMarketplace?.program) {
    type = 'program';
  } else if (apiMarketplace?.meal) {
    type = 'meal';
  } else if (apiMarketplace?.nutritionPlan) {
    type = 'nutritionPlan';
  } else if (apiMarketplace?.episode) {
    type = 'episode';
  } else if (apiMarketplace?.module) {
    type = 'module';
  } else if (apiMarketplace?.course) {
    type = 'course';
  }

  if (
    !apiMarketplace.program &&
    !apiMarketplace.workout &&
    !apiMarketplace.exercise &&
    !apiMarketplace.meal &&
    !apiMarketplace.nutritionPlan &&
    !apiMarketplace.episode &&
    !apiMarketplace.module &&
    !apiMarketplace.course
  ) {
    return null;
  }

  return {
    id: apiMarketplace.id,
    author:
      apiMarketplace?.exercise?.author ||
      apiMarketplace.workout?.author ||
      apiMarketplace.program?.author ||
      apiMarketplace.meal?.author ||
      apiMarketplace.nutritionPlan?.author ||
      apiMarketplace.episode?.author ||
      apiMarketplace.module?.author ||
      apiMarketplace.course?.author,
    averageKcalPerDay: apiMarketplace?.nutritionPlan?.averageKcalPerDay,
    categories: mapCategoryWithTagsFromApi({
      categories,
      locale,
    }),
    courseId: apiMarketplace?.course?.id,
    coverSignedUrl:
      apiMarketplace?.exercise?.coverSignedUrl ||
      apiMarketplace?.workout?.coverSignedUrl ||
      apiMarketplace?.program?.coverSignedUrl ||
      apiMarketplace?.meal?.coverSignedUrl ||
      apiMarketplace?.nutritionPlan?.coverSignedUrl ||
      apiMarketplace?.episode?.coverSignedUrl ||
      apiMarketplace?.module?.coverSignedUrl ||
      apiMarketplace?.course?.coverSignedUrl ||
      '',
    currency: apiMarketplace.currency,
    episodeId: apiMarketplace?.episode?.id,
    exerciseId: apiMarketplace?.exercise?.id,
    inLibrary: Boolean(
      apiMarketplace?.workout?.isInLibrary ||
      apiMarketplace?.program?.isInLibrary ||
      apiMarketplace?.exercise?.isInLibrary ||
      apiMarketplace?.meal?.isInLibrary ||
      apiMarketplace?.nutritionPlan?.isInLibrary ||
      apiMarketplace?.episode?.isInLibrary ||
      apiMarketplace?.module?.isInLibrary ||
      apiMarketplace?.course?.isInLibrary,
    ),
    restorable: Boolean(
      apiMarketplace?.workout?.isRestorable ||
      apiMarketplace?.program?.isRestorable ||
      apiMarketplace?.exercise?.isRestorable ||
      apiMarketplace?.meal?.isRestorable ||
      apiMarketplace?.nutritionPlan?.isRestorable ||
      apiMarketplace?.episode?.isRestorable ||
      apiMarketplace?.module?.isRestorable ||
      apiMarketplace?.course?.isRestorable,
    ),
    isCourseDeleted: apiMarketplace?.course?.isDeleted ?? false,
    isEpisodeDeleted: apiMarketplace?.episode?.isDeleted ?? false,
    isExerciseDeleted: apiMarketplace?.exercise?.isDeleted ?? false,
    isMealDeleted: apiMarketplace?.meal?.isDeleted ?? false,
    isModuleDeleted: apiMarketplace?.module?.isDeleted ?? false,
    isNutritionPlanDeleted: apiMarketplace?.nutritionPlan?.isDeleted ?? false,
    isProgramDeleted: apiMarketplace?.program?.isDeleted ?? false,
    isWorkoutDeleted: apiMarketplace?.workout?.isDeleted ?? false,
    marketplaceItemId: apiMarketplace.marketplaceItemId,
    isPremiumFavorite: apiMarketplace.savedSource === 'premium',
    level:
      apiMarketplace?.exercise?.level ??
      apiMarketplace?.workout?.level ??
      apiMarketplace?.program?.level,
    libraryId:
      apiMarketplace?.exercise?.libraryId ||
      apiMarketplace?.workout?.libraryId ||
      apiMarketplace?.program?.libraryId ||
      apiMarketplace?.meal?.libraryId ||
      apiMarketplace?.nutritionPlan?.libraryId ||
      apiMarketplace?.episode?.libraryId ||
      apiMarketplace?.module?.libraryId ||
      apiMarketplace?.course?.libraryId,
    mealId: apiMarketplace?.meal?.id,
    mediaSignedUrl:
      apiMarketplace?.exercise?.mediaSignedUrl ||
      apiMarketplace?.meal?.mediaSignedUrl ||
      '',
    moduleId: apiMarketplace?.module?.id,
    numberOfExercises:
      apiMarketplace?.workout?.numberOfExercises ??
      apiMarketplace?.program?.numberOfExercises,
    numberOfWeeks:
      apiMarketplace?.program?.numberOfWeeks ??
      apiMarketplace?.nutritionPlan?.numberOfWeeks,
    numberOfWorkouts: apiMarketplace?.program?.numberOfWorkouts,
    nutritionPlanId: apiMarketplace?.nutritionPlan?.id,
    organizationId,
    price: apiMarketplace.price,
    programId: apiMarketplace?.program?.id,
    totalFat: apiMarketplace?.meal?.totalFat,
    totalKcal:
      apiMarketplace?.meal?.totalKcal ??
      apiMarketplace?.nutritionPlan?.totalKcal,
    totalProtein: apiMarketplace?.meal?.totalProtein,
    totalUH: apiMarketplace?.meal?.totalUH,
    translations: mapTranslationsFromApi(
      (translations ?? []) as ApiTranslation[],
    ),
    type,
    workoutId: apiMarketplace?.workout?.id,
  };
}
